/**
 * Best-effort conversion of a Python repr string to a parsed JavaScript value.
 *
 * Handles the common differences between Python's str()/repr() output and JSON:
 *   - None → null, True → true, False → false (only outside string literals)
 *   - Single-quoted strings → double-quoted strings
 *   - Double quotes inside single-quoted strings are escaped
 *
 * Returns the parsed object/array, or undefined if the string cannot be parsed
 * even after normalization.
 */
export default function parsePythonReprToJson(value: string): object | undefined {
  const jsonString = normalizePythonToJson(value);

  try {
    const parsed = JSON.parse(jsonString);
    if (typeof parsed === 'object' && parsed != null) {
      return parsed;
    }
  } catch (e: unknown) {
    if (!(e instanceof SyntaxError)) {
      throw e;
    }
  }
  return undefined;
}

const PYTHON_KEYWORDS: ReadonlyMap<string, string> = new Map([
  ['None', 'null'],
  ['True', 'true'],
  ['False', 'false'],
]);

function tryMatchKeyword(value: string, pos: number): {replacement: string; length: number} | undefined {
  for (const [keyword, replacement] of PYTHON_KEYWORDS) {
    if (
      value.startsWith(keyword, pos) &&
      !isWordChar(value[pos - 1]) &&
      !isWordChar(value[pos + keyword.length])
    ) {
      return {replacement, length: keyword.length};
    }
  }
  return undefined;
}

function isWordChar(ch: string | undefined): boolean {
  if (ch == null) {
    return false;
  }
  return /\w/.test(ch);
}

/**
 * A character is escaped if preceded by an odd number of backslashes.
 * e.g. \' is escaped, \\' is not (the backslash itself is escaped).
 */
function isEscaped(value: string, pos: number): boolean {
  let count = 0;
  for (let i = pos - 1; i >= 0 && value[i] === '\\'; i--) {
    count++;
  }
  return count % 2 === 1;
}

/**
 * Single-pass walk that converts Python repr syntax to JSON:
 * - Replaces single-quote string delimiters with double quotes
 * - Escapes double quotes inside single-quoted strings
 * - Replaces None/True/False keywords with null/true/false only when outside strings
 */
function normalizePythonToJson(value: string): string {
  let out = '';
  let inString = false;
  let delimiter = '';

  for (let i = 0; i < value.length; i++) {
    const ch = value[i];

    if (!inString) {
      const keywordMatch = tryMatchKeyword(value, i);
      if (keywordMatch != null) {
        out += keywordMatch.replacement;
        i += keywordMatch.length - 1;
      } else if (ch === "'" || ch === '"') {
        delimiter = ch;
        inString = true;
        out += '"';
      } else {
        out += ch;
      }
    } else if (ch === delimiter && !isEscaped(value, i)) {
      inString = false;
      out += '"';
    } else if (ch === '"' && delimiter === "'") {
      out += '\\"';
    } else if (
      ch === '\\' &&
      i + 1 < value.length &&
      value[i + 1] === "'" &&
      delimiter === "'" &&
      !isEscaped(value, i)
    ) {
      // \' in a single-quoted Python string → unescaped ' in a double-quoted JSON string
      out += "'";
      i++;
    } else {
      out += ch;
    }
  }

  return out;
}
